import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

export interface AccessControlResult {
  hasAccess: boolean;
  reason: 'authenticated' | 'subscribed' | 'enrolled' | 'error' | 'none';
  message: string;
  requiredTier?: string;
  currentTier?: string;
}

export interface UserAccessStatus {
  subscriptionTier: 'Free' | 'Basic' | 'Pro';
  accessLevel: string;
  features: {
    freeLessons: boolean;
    allCourseLessons: boolean;
    liveSessions: boolean;
    qa: boolean;
  };
}

export const useEnhancedAccessControl = () => {
  const { user, isAuthenticated } = useAuth();
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessReason, setAccessReason] = useState<AccessControlResult['reason']>('none');
  const [accessMessage, setAccessMessage] = useState('');
  const [userAccessStatus, setUserAccessStatus] = useState<UserAccessStatus | null>(null);

  const checkAccess = async (
    feature: 'free_lessons' | 'all_course_lessons' | 'live_sessions' | 'qa' | 'courses' = 'courses',
    courseId?: string,
    lessonId?: string
  ): Promise<AccessControlResult> => {
    // Check authentication
    if (!isAuthenticated || !user) {
      setAccessReason('authenticated');
      setAccessMessage('Please login to access this feature');
      return {
        hasAccess: false,
        reason: 'authenticated',
        message: 'Please login to access this feature'
      };
    }

    try {
      let response;
      
      if (feature === 'live_sessions') {
        response = await api.get('/subscriptions/access/live-sessions');
      } else if (feature === 'all_course_lessons') {
        response = await api.get('/subscriptions/access/course-lessons');
      } else if (feature === 'free_lessons') {
        response = await api.get('/subscriptions/access/free-lessons');
      } else if (feature === 'qa') {
        response = await api.get('/subscriptions/access/qa');
      } else if (courseId && lessonId) {
        // Check specific lesson access
        response = await api.get(`/courses/${courseId}/access/lesson/${lessonId}`);
      } else if (courseId) {
        // Check course access status
        response = await api.get(`/courses/${courseId}/access/status`);
      } else {
        // Get general access status
        response = await api.get('/subscriptions/access/status');
      }

      const accessData = response.data;
      
      if (!accessData.hasAccess) {
        setAccessReason('subscribed');
        setAccessMessage(accessData.message || 'Subscription required');
        return {
          hasAccess: false,
          reason: 'subscribed',
          message: accessData.message || 'Subscription required',
          requiredTier: accessData.requiredTier,
          currentTier: accessData.currentTier
        };
      }

      return {
        hasAccess: true,
        reason: 'none',
        message: 'Access granted'
      };
    } catch (error) {
      console.error('Error checking access:', error);
      setAccessReason('error');
      setAccessMessage('Error checking access permissions');
      return {
        hasAccess: false,
        reason: 'error',
        message: 'Error checking access permissions'
      };
    }
  };

  const getUserAccessStatus = async (): Promise<UserAccessStatus | null> => {
    if (!isAuthenticated || !user) {
      return null;
    }

    try {
      const response = await api.get('/subscriptions/access/status');
      const accessData = response.data;
      setUserAccessStatus(accessData);
      return accessData;
    } catch (error) {
      console.error('Error getting user access status:', error);
      return null;
    }
  };

  const checkLiveSessionAccess = async (classId?: string): Promise<AccessControlResult> => {
    if (!isAuthenticated || !user) {
      setAccessReason('authenticated');
      setAccessMessage('Please login to access live sessions');
      return {
        hasAccess: false,
        reason: 'authenticated',
        message: 'Please login to access live sessions'
      };
    }

    try {
      let endpoint = '/subscriptions/access/live-sessions';
      if (classId) {
        endpoint = `/live-classes/${classId}/access/join`;
      }
      
      const response = await api.get(endpoint);
      const accessData = response.data;
      
      if (!accessData.hasAccess) {
        setAccessReason('subscribed');
        setAccessMessage(accessData.message || 'Pro subscription required for live sessions');
        return {
          hasAccess: false,
          reason: 'subscribed',
          message: accessData.message || 'Pro subscription required for live sessions',
          requiredTier: accessData.requiredSubscription || 'Pro',
          currentTier: accessData.currentTier
        };
      }

      return {
        hasAccess: true,
        reason: 'none',
        message: 'Access granted'
      };
    } catch (error) {
      console.error('Error checking live session access:', error);
      setAccessReason('error');
      setAccessMessage('Error checking live session access');
      return {
        hasAccess: false,
        reason: 'error',
        message: 'Error checking live session access'
      };
    }
  };

  const checkCourseLessonAccess = async (courseId: string, lessonId?: string): Promise<AccessControlResult> => {
    if (!isAuthenticated || !user) {
      setAccessReason('authenticated');
      setAccessMessage('Please login to access course content');
      return {
        hasAccess: false,
        reason: 'authenticated',
        message: 'Please login to access course content'
      };
    }

    try {
      let endpoint;
      if (lessonId) {
        endpoint = `/courses/${courseId}/access/lesson/${lessonId}`;
      } else {
        endpoint = `/courses/${courseId}/access/status`;
      }
      
      const response = await api.get(endpoint);
      const accessData = response.data;
      
      if (!accessData.hasAccess) {
        setAccessReason('subscribed');
        setAccessMessage(accessData.message || 'Subscription required for this content');
        return {
          hasAccess: false,
          reason: 'subscribed',
          message: accessData.message || 'Subscription required for this content',
          requiredTier: accessData.requiredTier,
          currentTier: accessData.currentTier
        };
      }

      return {
        hasAccess: true,
        reason: 'none',
        message: 'Access granted'
      };
    } catch (error) {
      console.error('Error checking course lesson access:', error);
      setAccessReason('error');
      setAccessMessage('Error checking course lesson access');
      return {
        hasAccess: false,
        reason: 'error',
        message: 'Error checking course lesson access'
      };
    }
  };

  // Legacy method for backward compatibility
  const checkLegacyAccess = async (
    requiresSubscription: boolean = false,
    requiresEnrollment: boolean = false,
    courseId?: string
  ): Promise<AccessControlResult> => {
    if (!isAuthenticated || !user) {
      setAccessReason('authenticated');
      setAccessMessage('Please login to access this feature');
      return {
        hasAccess: false,
        reason: 'authenticated',
        message: 'Please login to access this feature'
      };
    }

    if (requiresSubscription) {
      return checkAccess('all_course_lessons');
    }

    if (requiresEnrollment && courseId) {
      try {
        const response = await api.get(`/courses/${courseId}/enrollment-status`);
        const isEnrolled = response.data?.isEnrolled || false;
        
        if (!isEnrolled) {
          setAccessReason('enrolled');
          setAccessMessage('Please enroll in this course to access the classroom');
          return {
            hasAccess: false,
            reason: 'enrolled',
            message: 'Please enroll in this course to access the classroom'
          };
        }
      } catch (error) {
        setAccessReason('enrolled');
        setAccessMessage('Please enroll in this course to access the classroom');
        return {
          hasAccess: false,
          reason: 'enrolled',
          message: 'Please enroll in this course to access the classroom'
        };
      }
    }

    return {
      hasAccess: true,
      reason: 'none',
      message: 'Access granted'
    };
  };

  const showAccessDeniedModal = (result: AccessControlResult) => {
    setAccessReason(result.reason);
    setAccessMessage(result.message);
    setShowAccessModal(true);
  };

  const hideAccessModal = () => {
    setShowAccessModal(false);
    setAccessReason('none');
    setAccessMessage('');
  };

  return {
    checkAccess,
    checkLiveSessionAccess,
    checkCourseLessonAccess,
    getUserAccessStatus,
    checkLegacyAccess,
    showAccessDeniedModal,
    hideAccessModal,
    showAccessModal,
    accessReason,
    accessMessage,
    userAccessStatus,
    user,
    isAuthenticated
  };
};
